import { auth } from "./firebase";

async function getAuthHeaders(token?: string): Promise<HeadersInit> {
  const idToken = token ?? (await auth.currentUser?.getIdToken());
  if (!idToken) {
    throw new Error("Not authenticated");
  }

  return {
    Authorization: `Bearer ${idToken}`,
    "Content-Type": "application/json",
  };
}

// Proxy through Next.js rewrites to avoid CORS issues
// /api/yfp/* → NEXT_PUBLIC_BANYAN_REST_API_URL/yfp/*
const API_PREFIX = "/api";

async function parseResponse<T>(response: Response): Promise<T> {
  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return undefined as T;
  }
  return response.json();
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string,
): Promise<T> {
  const headers = await getAuthHeaders(token);
  const url = `${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(
      `API request failed (${response.status}): ${errorBody || response.statusText}`,
    );
  }

  return parseResponse<T>(response);
}

export const api = {
  get: <T>(endpoint: string, token?: string) =>
    apiRequest<T>(endpoint, { method: "GET" }, token),
  post: <T>(endpoint: string, data?: unknown, token?: string) =>
    apiRequest<T>(
      endpoint,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
      token,
    ),
  put: <T>(endpoint: string, data?: unknown, token?: string) =>
    apiRequest<T>(
      endpoint,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
      token,
    ),
  delete: <T>(endpoint: string, token?: string) =>
    apiRequest<T>(endpoint, { method: "DELETE" }, token),
};
